import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { WS_URL, API_KEY } from '../lib/api';
import { LiveEvent, WsEvent } from '../types';

const MAX_EVENTS = 100;

export function useSocket() {
  const socketRef              = useRef<Socket | null>(null);
  const [connected, setConnected]   = useState(false);
  const [events, setEvents]         = useState<LiveEvent[]>([]);
  const [lastEnrichment, setLastEnrichment] = useState<any>(null);
  const [lastConsent, setLastConsent]       = useState<any>(null);

  const addEvent = useCallback((ev: LiveEvent) => {
    setEvents(prev => [ev, ...prev].slice(0, MAX_EVENTS));
  }, []);

  useEffect(() => {
    const socket = io(`${WS_URL}/dashboard`, {
      extraHeaders: { 'X-API-Key': API_KEY },
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect',    () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('signal:received', (data: any) => {
      addEvent({
        id:        crypto.randomUUID(),
        type:      'signal',
        sessionId: data.sessionId,
        ts:        data.ts,
        label:     'New visitor signal',
        sublabel:  `Score: ${data.score ?? '—'}`,
        color:     '#6366f1',
      });
    });

    socket.on('enrichment:complete', (data: any) => {
      setLastEnrichment(data);
      addEvent({
        id:        crypto.randomUUID(),
        type:      'enrichment',
        sessionId: data.sessionId,
        ts:        data.ts,
        label:     data.person ?? data.company ?? 'Visitor identified',
        sublabel:  `${data.confidence}% confidence · ${data.sources?.join(', ')}`,
        color:     '#0ea5e9',
      });
    });

    socket.on('popup:shown', (data: any) => {
      addEvent({
        id:        crypto.randomUUID(),
        type:      'popup',
        sessionId: data.sessionId,
        ts:        data.ts,
        label:     'Popup shown',
        sublabel:  data.headline,
        color:     '#f59e0b',
      });
    });

    socket.on('consent:received', (data: any) => {
      setLastConsent(data);
      addEvent({
        id:        crypto.randomUUID(),
        type:      'consent',
        sessionId: data.sessionId,
        ts:        data.ts,
        label:     `🔥 ${data.person ?? 'Visitor'} @ ${data.company ?? '—'}`,
        sublabel:  `Consented via ${data.channel}`,
        color:     '#22c55e',
      });
    });

    socket.on('outreach:sent', (data: any) => {
      addEvent({
        id:        crypto.randomUUID(),
        type:      'outreach',
        sessionId: data.sessionId,
        ts:        data.ts,
        label:     `Outreach sent via ${data.channel}`,
        sublabel:  `Status: ${data.status}`,
        color:     '#a855f7',
      });
    });

    return () => { socket.disconnect(); };
  }, [addEvent]);

  return { connected, events, lastEnrichment, lastConsent };
}
